import {type AxiosError, isAxiosError} from "axios";
import toast from "react-hot-toast";
import type {IntlShape} from "react-intl";

export class NonRetryableError extends Error {
    public readonly originalError: AxiosError;
    public readonly isAxiosError = true;

    constructor(originalError: AxiosError) {
        super(originalError.message);
        this.name = "NonRetryableError";
        this.originalError = originalError;
    }

    get response() {
        return this.originalError.response;
    }

    get config() {
        return this.originalError.config;
    }

    get code() {
        return this.originalError.code;
    }

    get request() {
        return this.originalError.request;
    }
}

export function isNonRetryableError(error: unknown): error is NonRetryableError {
    return error instanceof NonRetryableError;
}

export function getErrorCode(error: unknown): string | null {
    if (!isAxiosError(error)) return null;
    return error?.response?.data?.code?.toLowerCase() ?? null;
}

export function isPriceExceededError(error: unknown): boolean {
    return getErrorCode(error) === "public_order_link_price_exceeded";
}

export function handlePublicHashError(
    error: unknown,
    intl: IntlShape,
    clearOrderHash: () => void
): void {
    if (!isAxiosError(error)) return;

    const errorCode = getErrorCode(error);

    if (errorCode === "public_link_depleted") {
        toast.error(
            intl.formatMessage({
                id: "public_link_depleted",
                defaultMessage:
                    "This link has reached its usage limit. No more orders are available.",
            })
        );
        clearOrderHash();
        throw new NonRetryableError(error);
    }

    if (errorCode === "not_found") {
        toast.error(
            intl.formatMessage({
                id: "public_link_not_found",
                defaultMessage: "This public order link is no longer valid.",
            })
        );
        clearOrderHash();
        throw new NonRetryableError(error);
    }

    if (errorCode === "public_order_link_price_exceeded") {
        // Don't show toast - will be shown as Alert in Overview
        // Don't clear hash - user might want to try a different route
        throw new NonRetryableError(error);
    }
}
